import React, { useCallback, useEffect, useState } from 'react';
import { CheckCircle, ChevronRight, FileSignature, FileText, Loader2, X } from 'lucide-react';
import SignaturePad from '../engagement/SignaturePad';
import { recordPortalDeliverableApproval } from '../../utils/portalDeliverableApprovalService';
import { initialsSignatureDataUrl, isValidApprovalInitials } from '../../utils/initialsSignature';
import { logPortalActivity } from '../../utils/portalActivityLogger';
import type { PortalFileRelease } from '../../types';
import './PortalDrawingsApprovalWalkthrough.css';

export function drawingApprovalLabel(release: Pick<PortalFileRelease, 'displayLabel' | 'name'>): string {
  return (release.displayLabel || release.name || 'Drawing').trim();
}

/** "Pool Awning and Lintels" / "A, B, and C". */
export function formatDrawingList(labels: string[]): string {
  const names = labels.map(label => label.trim()).filter(Boolean);
  if (names.length === 0) return 'these drawings';
  if (names.length === 1) return names[0]!;
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
}

export interface PortalDrawingsApprovalWalkthroughProps {
  open: boolean;
  onClose: () => void;
  portalId: string;
  dealId: string;
  releases: PortalFileRelease[];
  bundleId?: string;
  releaseVersionIndex?: number;
  activitySource?: 'client' | 'employee' | 'stakeholder';
  /** Opens the pop-up PDF viewer for this drawing. The walkthrough stays mounted underneath. */
  onOpenDrawing: (release: PortalFileRelease) => void;
  /** Release currently open in the viewer, so Advance to next file stays in step. */
  viewingReleaseId?: string | null;
  /** Initials modal opened from Approve on the last file. */
  initialsModalOpen?: boolean;
  onInitialsModalClose?: () => void;
  /** Closes the viewer and this walkthrough after the initials approval is recorded. */
  onApprovalFinished?: () => void;
  onApproved?: () => void;
}

type Step = 'intro' | 'review' | 'sign' | 'done';

const PortalDrawingsApprovalWalkthrough: React.FC<PortalDrawingsApprovalWalkthroughProps> = ({
  open,
  onClose,
  portalId,
  dealId,
  releases,
  bundleId,
  releaseVersionIndex,
  activitySource = 'client',
  onOpenDrawing,
  viewingReleaseId,
  initialsModalOpen = false,
  onInitialsModalClose,
  onApprovalFinished,
  onApproved,
}) => {
  const [step, setStep] = useState<Step>('intro');
  const [index, setIndex] = useState(0);
  const [reviewed, setReviewed] = useState<Set<string>>(() => new Set());
  const [signatureData, setSignatureData] = useState<{ dataUrl: string; signedBy: string } | null>(null);
  const [initials, setInitials] = useState('');
  const [initialsDone, setInitialsDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const labels = releases.map(drawingApprovalLabel);
  const listLabel = formatDrawingList(labels);
  const current = releases[index] ?? null;
  const reviewedCurrent = !!(current?.id && reviewed.has(current.id));
  const isLastDrawing = index >= releases.length - 1;

  useEffect(() => {
    if (!open) return;
    setStep('intro');
    setIndex(0);
    setReviewed(new Set());
    setSignatureData(null);
    setInitials('');
    setInitialsDone(false);
    setSubmitting(false);
    setError(null);
  }, [open]);

  useEffect(() => {
    if (!initialsModalOpen) return;
    setInitials('');
    setInitialsDone(false);
    setError(null);
  }, [initialsModalOpen]);

  useEffect(() => {
    if (!open || !viewingReleaseId) return;
    const nextIndex = releases.findIndex(release => release.id === viewingReleaseId);
    if (nextIndex < 0) return;
    setIndex(nextIndex);
    setStep(currentStep => (currentStep === 'intro' || currentStep === 'review' ? 'review' : currentStep));
    setReviewed(prev => {
      if (prev.has(viewingReleaseId)) return prev;
      const next = new Set(prev);
      next.add(viewingReleaseId);
      return next;
    });
  }, [open, viewingReleaseId, releases]);

  const openDrawing = useCallback(
    (release: PortalFileRelease) => {
      if (release.id) {
        setReviewed(prev => {
          const next = new Set(prev);
          next.add(release.id!);
          return next;
        });
      }
      onOpenDrawing(release);
    },
    [onOpenDrawing],
  );

  const goToDrawing = (nextIndex: number) => {
    const release = releases[nextIndex];
    if (!release) return;
    setError(null);
    setIndex(nextIndex);
    setStep('review');
    openDrawing(release);
  };

  const handleSubmit = async (signature?: { dataUrl: string; signedBy: string }, fromInitialsModal = false) => {
    const signed = signature ?? signatureData;
    if (!signed || releases.length === 0) return;
    const primary = releases[0]!;
    const releaseIds = releases.map(release => release.id || '').filter(Boolean);
    setSubmitting(true);
    setError(null);
    try {
      await recordPortalDeliverableApproval({
        portalId,
        dealId,
        deliverableType: 'plans',
        signature: signed,
        releaseId: primary.id,
        bundleId,
        releaseVersionIndex: releaseVersionIndex ?? primary.releaseVersionIndex,
        releaseIds,
      });
      void logPortalActivity(
        portalId,
        dealId,
        'action',
        'approved_engineered_drawings',
        {
          releaseIds,
          bundleId,
          releaseVersionIndex: releaseVersionIndex ?? primary.releaseVersionIndex,
          context: 'drawings_approval_walkthrough',
        },
        activitySource,
      );
      onApproved?.();
      if (fromInitialsModal) setInitialsDone(true);
      else setStep('done');
    } catch (e) {
      console.error(e);
      setError('Could not record your approval. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open || releases.length === 0) return null;

  const finishInitials = () => {
    onInitialsModalClose?.();
    if (initialsDone) onApprovalFinished?.();
  };

  const stepCount = releases.length + 1;
  const currentStep = step === 'sign' || step === 'done' ? stepCount : step === 'review' ? index + 1 : 0;

  return (
    <>
    {initialsModalOpen ? null : (
    <div className="pdraw-overlay" role="presentation">
      <div
        className="pdraw-shell"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pdraw-title"
      >
        <header className="pdraw-header">
          <div>
            <h2 id="pdraw-title">
              <FileSignature size={18} aria-hidden />
              Approve drawings
            </h2>
            {step === 'review' || step === 'sign' ? (
              <p className="pdraw-progress">
                Step {currentStep} of {stepCount}
              </p>
            ) : null}
          </div>
          <button type="button" className="pdraw-close" onClick={onClose} aria-label="Close" disabled={submitting}>
            <X size={18} />
          </button>
        </header>

        {step === 'intro' && (
          <div className="pdraw-body">
            <p className="pdraw-lead">
              This approval covers {listLabel}. You&apos;ll review each drawing, then sign once for the
              whole set.
            </p>
            <ol className="pdraw-list">
              {releases.map(release => (
                <li key={release.id || release.releasedUrl}>
                  <FileText size={14} aria-hidden />
                  {drawingApprovalLabel(release)}
                </li>
              ))}
            </ol>
            <footer className="pdraw-footer">
              <button type="button" className="pdraw-secondary" onClick={onClose}>
                Not now
              </button>
              <button type="button" className="pdraw-primary" onClick={() => goToDrawing(0)}>
                Review
                <ChevronRight size={16} />
              </button>
            </footer>
          </div>
        )}

        {step === 'review' && current && (
          <div className="pdraw-body">
            <p className="pdraw-kicker">
              Drawing {index + 1} of {releases.length}
            </p>
            <h3 className="pdraw-drawing-name">{drawingApprovalLabel(current)}</h3>
            <p className="pdraw-lead">
              {isLastDrawing
                ? 'This is the last file. Use Approve at the bottom of the viewer when you are ready to sign with your initials.'
                : 'Look this file over in the viewer. Use Advance to next file at the bottom of the viewer when you are ready for the next one.'}
            </p>
            <footer className="pdraw-footer">
              <button
                type="button"
                className="pdraw-secondary"
                onClick={() => {
                  if (index === 0) setStep('intro');
                  else goToDrawing(index - 1);
                }}
              >
                Back
              </button>
              <button type="button" className="pdraw-secondary" onClick={() => openDrawing(current)}>
                Open drawing
              </button>
              <button
                type="button"
                className="pdraw-primary"
                onClick={() => {
                  if (isLastDrawing) setStep('sign');
                  else goToDrawing(index + 1);
                }}
                disabled={!reviewedCurrent}
              >
                {isLastDrawing ? 'Continue to sign' : `Next: ${labels[index + 1] || 'drawing'}`}
                <ChevronRight size={16} />
              </button>
            </footer>
          </div>
        )}

        {step === 'sign' && (
          <div className="pdraw-body">
            <p className="pdraw-lead">
              By signing, you confirm that you have reviewed {listLabel} and approve{' '}
              {releases.length === 1 ? 'this drawing' : 'these drawings'} for your project. Your
              project team will be notified.
            </p>
            <SignaturePad hideSubmitButton onChange={setSignatureData} onSign={setSignatureData} />
            {error ? (
              <p className="pdraw-error" role="alert">
                {error}
              </p>
            ) : null}
            <footer className="pdraw-footer">
              <button
                type="button"
                className="pdraw-secondary"
                onClick={() => {
                  setIndex(releases.length - 1);
                  setStep('review');
                }}
                disabled={submitting}
              >
                Back
              </button>
              <button
                type="button"
                className="pdraw-primary"
                onClick={() => void handleSubmit()}
                disabled={!signatureData || submitting}
              >
                {submitting ? <Loader2 size={16} className="pdraw-spin" /> : null}
                Sign &amp; Approve
              </button>
            </footer>
          </div>
        )}

        {step === 'done' && (
          <div className="pdraw-body pdraw-done">
            <CheckCircle size={40} aria-hidden />
            <p>Thank you — your approval of {listLabel} has been recorded.</p>
            <button type="button" className="pdraw-primary" onClick={onClose}>
              Close
            </button>
          </div>
        )}
      </div>
    </div>
    )}
    {initialsModalOpen ? (
      <div className="pdraw-initials-overlay" role="presentation">
        <div
          className="pdraw-shell"
          role="dialog"
          aria-modal="true"
          aria-labelledby="pdraw-initials-title"
        >
          <header className="pdraw-header">
            <h2 id="pdraw-initials-title">
              <FileSignature size={18} aria-hidden />
              Approve drawings
            </h2>
            <button
              type="button"
              className="pdraw-close"
              onClick={finishInitials}
              aria-label="Close"
              disabled={submitting}
            >
              <X size={18} />
            </button>
          </header>
          {initialsDone ? (
            <div className="pdraw-body pdraw-done">
              <CheckCircle size={40} aria-hidden />
              <p>Thank you — your approval of {listLabel} has been recorded.</p>
              <button type="button" className="pdraw-primary" onClick={finishInitials}>
                Close
              </button>
            </div>
          ) : (
            <div className="pdraw-body">
              <p className="pdraw-lead">
                Type your initials to approve {listLabel}. Your initials act as your electronic
                signature and are legally binding.
              </p>
              <label className="pdraw-initials-label" htmlFor="pdraw-initials-input">
                Your initials
              </label>
              <input
                id="pdraw-initials-input"
                type="text"
                className="pdraw-initials-input"
                value={initials}
                onChange={event => setInitials(event.target.value.toUpperCase().slice(0, 8))}
                placeholder="e.g. BD"
                autoComplete="name"
                disabled={submitting}
                maxLength={8}
                autoFocus
              />
              {error ? (
                <p className="pdraw-error" role="alert">
                  {error}
                </p>
              ) : null}
              <footer className="pdraw-footer">
                <button
                  type="button"
                  className="pdraw-secondary"
                  onClick={finishInitials}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="pdraw-primary"
                  onClick={() => {
                    if (!isValidApprovalInitials(initials)) return;
                    const signedBy = initials.trim().toUpperCase();
                    void handleSubmit(
                      { dataUrl: initialsSignatureDataUrl(signedBy), signedBy },
                      true,
                    );
                  }}
                  disabled={!isValidApprovalInitials(initials) || submitting}
                >
                  {submitting ? <Loader2 size={16} className="pdraw-spin" /> : null}
                  Approve
                </button>
              </footer>
            </div>
          )}
        </div>
      </div>
    ) : null}
    </>
  );
};

export default PortalDrawingsApprovalWalkthrough;

